import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/routing";
import { ListingFilters } from "@/lib/fetch/listing";
import { ArrowRight, Map } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import SearchInput from "./SearchInput";
import SearchSortSelect from "./SearchSortSelect";

type Props = {
  search: ListingFilters["search"];
  setSearch: (search: ListingFilters["search"]) => void;
  sort: ListingFilters["sort"];
  setSort: (sort: ListingFilters["sort"]) => void;
  paramString: string;
};

/** The search / map / sort controls row at the top of the results view. */
const SearchControls: React.FC<Props> = ({
  search,
  setSearch,
  sort,
  setSort,
  paramString,
}) => {
  const [currentSearch, setCurrentSearch] = useState<string>(search || "");
  const t = useTranslations("search");

  useEffect(() => {
    setCurrentSearch(search || "");
  }, [search]);

  const onSearch = () => {
    setSearch(currentSearch);
  };

  return (
    <div className="google-anno-skip flex flex-row gap-2">
      <div className="min-w-0 flex-1">
        <SearchInput
          search={currentSearch}
          setSearch={setCurrentSearch}
          onSearch={onSearch}
        />
      </div>

      <Button
        aria-label={t("search")}
        type="button"
        onClick={onSearch}
        disabled={!currentSearch}
        className="w-10 disabled:opacity-100 md:w-fit"
      >
        <span className="hidden md:block">{t("search")}</span>
        <ArrowRight className="size-4 md:hidden" />
      </Button>

      <Button variant="outline" type="button" asChild className="w-10 md:w-fit">
        <Link
          aria-label={t("view-map")}
          href={paramString.length ? `/map?${paramString}` : "/map"}
        >
          <Map className="size-4 md:hidden" />
          <span className="hidden md:block">{t("view-map")}</span>
        </Link>
      </Button>

      <SearchSortSelect sort={sort} setSort={setSort} />
    </div>
  );
};

export default SearchControls;
